import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth, hasFxAccess } from './components/auth/AuthContext';
import { useSystemDateRollover } from './hooks/useSystemDateRollover';
import { useTempAccessWarning } from './hooks/useTempAccessWarning';
import Layout from './components/common/Layout';
import HomePage from './pages/client/HomePage';
import TradeBooking from './pages/client/TradeBooking';
import ForwardMaturity from './pages/client/ForwardMaturity';
import ContractComparison from './pages/client/ContractComparison';
import Confirmations from './pages/client/Confirmations';
import CashflowManagement from './pages/client/CashflowManagement';
import BankDashboard from './pages/bank/BankDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import Accounting from './pages/admin/Accounting';
import ClassifyEmails from './pages/admin/ClassifyEmails';
import EvalReview from './pages/admin/EvalReview';
import FXAnalytics from './pages/bank/FXAnalytics';
import ConfirmationsReport from './pages/client/ConfirmationsReport';
import AccountingReport from './pages/client/AccountingReport';
import Valuation from './pages/client/Valuation';
import Positions from './pages/client/Positions';
import CustomDocuments from './pages/client/CustomDocuments';
import CustomReports from './pages/client/CustomReports';
import Modifications from './pages/client/Modifications';
import CommodityPositions from './pages/client/commodities/CommodityPositions';
import CommodityAdmin from './pages/client/commodities/CommodityAdmin';
import CommodityTradeBooking from './pages/client/commodities/CommodityTradeBooking';
import CommodityExpiryUpload from './pages/client/commodities/CommodityExpiryUpload';
import CommodityExpiry from './pages/client/commodities/CommodityExpiry';
import CommodityValuation from './pages/client/commodities/CommodityValuation';
import CommodityCustomReports from './pages/client/commodities/CommodityCustomReports';
import CommodityModifications from './pages/client/commodities/CommodityModifications';
import CommodityHomePage from './pages/client/commodities/CommodityHomePage';
import Login from './pages/auth/Login';
import './styles/App.css';

/**
 * Full-screen non-dismissable overlay shown during system date rollover.
 * Only mounted when the user is authenticated (clientId is always defined).
 *
 *   system_date_rollover_warning → amber warning modal (~5 min before midnight)
 *   system_date_force_logout     → "Cerrando sesión..." then auto-logout
 */
const SystemDateRolloverGuard: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { t } = useTranslation();
  const { showRolloverWarning, forcingLogout } = useSystemDateRollover(clientId);
  const [dismissed, setDismissed] = React.useState(false);

  // Force-logout modal is never dismissable; warning can be dismissed so the
  // user can save their work — the force-logout SSE will still fire at midnight.
  const visible = forcingLogout || (showRolloverWarning && !dismissed);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 9999,
      backgroundColor: 'rgba(0, 0, 0, 0.82)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        backgroundColor: '#1c1c2e',
        border: '1px solid rgba(255, 170, 0, 0.5)',
        borderRadius: '12px',
        padding: '48px 56px',
        maxWidth: '500px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
        position: 'relative',
      }}>
        {!forcingLogout && (
          <button
            onClick={() => setDismissed(true)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '16px',
              background: 'none',
              border: 'none',
              color: '#888',
              fontSize: '20px',
              cursor: 'pointer',
              lineHeight: 1,
              padding: '4px',
            }}
            aria-label="Cerrar"
          >
            ×
          </button>
        )}
        <div style={{ fontSize: '40px', marginBottom: '20px' }}>
          {forcingLogout ? '⏳' : '⚠️'}
        </div>
        <p style={{
          color: forcingLogout ? '#fff' : '#ffaa00',
          fontSize: '15px',
          lineHeight: '1.6',
          margin: 0,
        }}>
          {forcingLogout ? t('systemDate.forcingLogout') : t('systemDate.rolloverWarning')}
        </p>
      </div>
    </div>
  );
};

/**
 * Dismissable warning banner shown ~5 min before temporary IP access expires.
 * Only mounted when the user is authenticated (KAN-204).
 */
const TempAccessWarningBanner: React.FC<{ clientId: string }> = ({ clientId }) => {
  const { showWarning, dismissWarning } = useTempAccessWarning(clientId);

  if (!showWarning) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9998,
      backgroundColor: '#ffaa00',
      color: '#1c1c2e',
      padding: '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      fontSize: '14px',
      fontWeight: 600,
      boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      <span>Su acceso temporal expira en 5 minutos. Guarde su trabajo.</span>
      <button
        onClick={dismissWarning}
        style={{
          background: 'rgba(0,0,0,0.15)',
          border: 'none',
          borderRadius: '4px',
          color: '#1c1c2e',
          padding: '4px 12px',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 600,
        }}
      >
        OK
      </button>
    </div>
  );
};

const ProtectedRoute: React.FC<{
  children: React.ReactNode;
  allowedRoles?: string[];
  requiredModule?: string;
}> = ({ children, allowedRoles, requiredModule }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="app-loading-spinner" />
        <p>{t('common.loading', 'Loading...')}</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Module-scoped route: check moduleRoles instead of the FX role
  if (requiredModule) {
    const moduleRole = user.moduleRoles?.[requiredModule];
    if (!moduleRole) {
      return <Navigate to="/" />;
    }
    if (allowedRoles && !allowedRoles.includes(moduleRole)) {
      return <Navigate to="/" />;
    }
    return <>{children}</>;
  }

  // FX route (default): check the top-level role field + FX access
  if (allowedRoles) {
    if (!hasFxAccess(user)) {
      // Non-FX user hitting an FX route → send to first available module
      const firstModule = user.moduleRoles ? Object.keys(user.moduleRoles)[0] : null;
      if (firstModule === 'Commodities') return <Navigate to="/commodities/home" />;
      return <Navigate to="/login" />;
    }
    if (!allowedRoles.includes(user.role)) {
      if (user.role === 'bank_admin') {
        return <Navigate to="/bank" />;
      } else {
        return <Navigate to="/" />;
      }
    }
  }

  return <>{children}</>;
};

function AppRoutes() {
  const { user } = useAuth();
  
  const getDefaultRoute = () => {
    if (!user) return "/login";
    if (user.role === 'bank_admin') return "/bank";
    // Commodities-only users (no FX access) → go to commodity positions
    if (!hasFxAccess(user) && user.moduleRoles?.Commodities) return "/commodities/home";
    return "/";
  };
  
  return (
    <Router>
      <div className="App">
        {/* Rollover warning / force-logout overlay — only active when logged in */}
        {user && (
          <>
            <SystemDateRolloverGuard clientId={user.organization?.id || user.id} />
            <TempAccessWarningBanner clientId={user.organization?.id || user.id} />
          </>
        )}
        <Routes>
          <Route path="/login" element={user ? <Navigate to={getDefaultRoute()} /> : <Login />} />
          <Route path="/" element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }>
            <Route index element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <HomePage />
              </ProtectedRoute>
            } />
            <Route path="confirmations" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <Confirmations />
              </ProtectedRoute>
            } />
            <Route path="client/trade-booking" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <TradeBooking />
              </ProtectedRoute>
            } />
            <Route path="forward-maturity" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <ForwardMaturity />
              </ProtectedRoute>
            } />
            <Route path="contract-comparison" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <ContractComparison />
              </ProtectedRoute>
            } />
            <Route path="cashflow-management" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CashflowManagement />
              </ProtectedRoute>
            } />
            <Route path="valuation" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <Valuation />
              </ProtectedRoute>
            } />
            <Route path="bank" element={
              <ProtectedRoute allowedRoles={['bank_admin']}>
                <BankDashboard />
              </ProtectedRoute>
            } />
            <Route path="admin" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_acc_user']}>
                <AdminDashboard />
              </ProtectedRoute>
            } />
            <Route path="classify-emails" element={
              <ProtectedRoute allowedRoles={['client_admin']}>
                <ClassifyEmails />
              </ProtectedRoute>
            } />
            <Route path="eval-review" element={
              <ProtectedRoute allowedRoles={['client_admin']}>
                <EvalReview />
              </ProtectedRoute>
            } />
            <Route path="accounting" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <Accounting />
              </ProtectedRoute>
            } />
            <Route path="fx-analytics" element={
              <ProtectedRoute allowedRoles={['bank_admin']}>
                <FXAnalytics />
              </ProtectedRoute>
            } />
            <Route path="reports/confirmations" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <ConfirmationsReport />
              </ProtectedRoute>
            } />
            <Route path="reports/accounting" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <AccountingReport />
              </ProtectedRoute>
            } />
            <Route path="reports/positions" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <Positions />
              </ProtectedRoute>
            } />
            <Route path="reports/custom-reports" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CustomReports />
              </ProtectedRoute>
            } />
            <Route path="modifications" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <Modifications />
              </ProtectedRoute>
            } />
            <Route path="custom-documents" element={
              <ProtectedRoute allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CustomDocuments />
              </ProtectedRoute>
            } />
            {/* Commodity routes (KAN-256) — requiredModule checks moduleRoles.Commodities */}
            <Route path="commodities/home" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityHomePage />
              </ProtectedRoute>
            } />
            <Route path="commodities/positions" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityPositions />
              </ProtectedRoute>
            } />
            <Route path="commodities/trade-booking" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityTradeBooking />
              </ProtectedRoute>
            } />
            <Route path="commodities/admin" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin']}>
                <CommodityAdmin />
              </ProtectedRoute>
            } />
            <Route path="commodities/expiry" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityExpiry />
              </ProtectedRoute>
            } />
            <Route path="commodities/expiry-upload" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin']}>
                <CommodityExpiryUpload />
              </ProtectedRoute>
            } />
            <Route path="commodities/valuation" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityValuation />
              </ProtectedRoute>
            } />
            <Route path="commodities/modifications" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityModifications />
              </ProtectedRoute>
            } />
            <Route path="commodities/custom-reports" element={
              <ProtectedRoute requiredModule="Commodities" allowedRoles={['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user']}>
                <CommodityCustomReports />
              </ProtectedRoute>
            } />
            </Route>
        </Routes>
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;